/**
 * Select Component
 * Reusable dropdown select with label and error support
 */

import { forwardRef } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

export interface SelectOption {
    value: string | number;
    label: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
    label?: string;
    error?: string;
    helperText?: string;
    options: SelectOption[];
    placeholder?: string;
    fullWidth?: boolean;
    size?: 'sm' | 'md' | 'lg';
    loading?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
    (
        {
            label,
            error,
            helperText,
            options,
            placeholder = 'Select an option',
            fullWidth = true,
            size = 'md',
            className = '',
            loading = false,
            disabled,
            ...props
        },
        ref
    ) => {
        const sizeClasses = {
            sm: 'py-1.5 text-sm',
            md: 'py-2.5 text-base',
            lg: 'py-3 text-lg',
        };

        const baseSelectClasses = `
            w-full pl-4 pr-10 rounded-lg border transition-colors appearance-none
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
            ${sizeClasses[size]}
            ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}
            ${disabled || loading ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-white cursor-pointer'}
        `.trim();

        return (
            <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
                {label && (
                    <label htmlFor={props.id} className="block text-sm font-medium text-gray-700 mb-1.5">
                        {label}
                    </label>
                )}
                <div className="relative">
                    {loading ? (
                        <div className="flex items-center justify-center py-2.5">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                    ) : (
                        <>
                            <select
                                ref={ref}
                                className={baseSelectClasses}
                                disabled={disabled || loading}
                                {...props}
                            >
                                <option value="">{placeholder}</option>
                                {options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                                <ChevronDown size={18} />
                            </div>
                        </>
                    )}
                </div>
                {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
                {helperText && !error && (
                    <p className="mt-1 text-sm text-gray-500">{helperText}</p>
                )}
            </div>
        );
    }
);

Select.displayName = 'Select';

export default Select;
